import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2, Star, RefreshCw } from "lucide-react";
import { Review } from "@/types/reviews";

interface ReviewsTableProps {
  isLoading: boolean;
  reviews: Review[];
  onDelete: (id: number) => void;
  onViewDetails: (review: Review) => void;
  sortColumn: string | null;
  sortDirection: "asc" | "desc";
  handleSort: (column: string | null) => void;
  renderSortIndicator: (column: string | null) => { __html: string } | null;
}

export const ReviewsTable = ({
  isLoading,
  reviews,
  onDelete,
  onViewDetails,
  sortColumn,
  sortDirection,
  handleSort,
  renderSortIndicator,
}: ReviewsTableProps) => {
  const renderStars = (rating: number) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
          }`}
        />
      ));
  };

  const renderHighlightBadge = (highlighted: boolean) => {
    // Make sure we're explicitly checking for true (not truthy values)
    if (highlighted === true) {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-3 w-3 mr-1 text-yellow-600" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M12 3l1.67 3.86 4.33.58-3.33 3.3.96 4.26-3.63-2.05-3.63 2.05.96-4.26L5 7.44l4.33-.58L12 3z" />
          </svg>
          Highlighted
        </div>
      );
    }
    return null;
  };

  const truncateComment = (comment: string | null) => {
    if (!comment) return "No comment";
    
    const COMMENT_PREVIEW_LENGTH = 30;
    if (comment.length <= COMMENT_PREVIEW_LENGTH) {
      return comment;
    }
    
    return `${comment.substring(0, COMMENT_PREVIEW_LENGTH).trim()}...`;
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead 
              onClick={() => handleSort('service_name')}
              className="cursor-pointer hover:bg-gray-50"
            >
              <div className="flex items-center">
                Service
                {renderSortIndicator('service_name') && (
                  <span dangerouslySetInnerHTML={renderSortIndicator('service_name')} />
                )}
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('customer_name')}
              className="cursor-pointer hover:bg-gray-50"
            >
              <div className="flex items-center">
                Customer
                {renderSortIndicator('customer_name') && (
                  <span dangerouslySetInnerHTML={renderSortIndicator('customer_name')} />
                )}
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('rating')}
              className="cursor-pointer hover:bg-gray-50 w-[120px]"
            >
              <div className="flex items-center">
                Rating
                {renderSortIndicator('rating') && (
                  <span dangerouslySetInnerHTML={renderSortIndicator('rating')} />
                )}
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('comment')}
              className="cursor-pointer hover:bg-gray-50"
            >
              <div className="flex items-center">
                Preview
                {renderSortIndicator('comment') && (
                  <span dangerouslySetInnerHTML={renderSortIndicator('comment')} />
                )}
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('created_at')}
              className="cursor-pointer hover:bg-gray-50 w-[100px]"
            >
              <div className="flex items-center">
                Date
                {renderSortIndicator('created_at') && (
                  <span dangerouslySetInnerHTML={renderSortIndicator('created_at')} />
                )}
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('highlighted')}
              className="cursor-pointer hover:bg-gray-50 w-[120px]"
            >
              <div className="flex items-center">
                Highlighted
                {renderSortIndicator('highlighted') && (
                  <span dangerouslySetInnerHTML={renderSortIndicator('highlighted')} />
                )}
              </div>
            </TableHead>
            <TableHead className="w-[60px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8">
                <div className="flex justify-center items-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin text-primary" />
                  <span>Loading reviews...</span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            reviews.length > 0 ? (
              reviews.map((review) => (
                <TableRow key={review.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">
                    <Link 
                      to={`/service/${review.service_id}`} 
                      className="text-primary hover:underline"
                    >
                      {review.service_name}
                    </Link>
                  </TableCell>
                  <TableCell>{review.customer_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {renderStars(review.rating)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {review.comment ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm truncate mr-2">
                          {truncateComment(review.comment)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewDetails(review)}
                          className="whitespace-nowrap"
                        >
                          View
                        </Button>
                      </div>
                    ) : (
                      <span className="text-gray-400 italic text-sm">No comment</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(review.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {renderHighlightBadge(review.highlighted)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDelete(review.id)}
                      title="Delete Review"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No reviews match your search criteria.
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};
